<template>
  <div class="gs-trigger-wrap">
    <!-- Trigger bar -->
    <div class="gs-trigger" @click="openModal">
      <i class="fa-solid fa-magnifying-glass gs-trigger-icon"></i>
      <span class="gs-trigger-text">Search…</span>
      <kbd class="gs-trigger-kbd">Ctrl K</kbd>
    </div>

    <!-- Modal -->
    <a-modal
      v-model:open="visible"
      :footer="null"
      :closable="false"
      :z-index="1200"
      width="660px"
      wrap-class-name="gs-modal-wrap"
      @afterClose="onModalClose"
    >
      <!-- Search input row -->
      <div class="gs-modal-header">
        <i class="fa-solid fa-magnifying-glass gs-modal-icon"></i>
        <input
          ref="inputRef"
          v-model="query"
          class="gs-modal-input"
          type="text"
          placeholder="Search employees, attendance, leaves, assets, letters…"
          autocomplete="off"
          @input="onInput"
          @keydown.esc="closeModal"
          @keydown.up.prevent="moveFocus(-1)"
          @keydown.down.prevent="moveFocus(1)"
          @keydown.enter.prevent="selectFocused"
        />
        <div class="gs-modal-header-right">
          <a-spin v-if="loading" size="small" />
          <button v-else-if="query" class="gs-icon-btn" title="Clear" @click="clearSearch">
            <i class="fa-solid fa-circle-xmark"></i>
          </button>
          <kbd class="gs-esc-kbd" @click="closeModal">Esc</kbd>
        </div>
      </div>

      <!-- Results body -->
      <div class="gs-modal-body" ref="modalBody">

        <!-- Idle -->
        <div v-if="!query" class="gs-state">
          <div class="gs-state-icon-ring">
            <i class="fa-solid fa-magnifying-glass"></i>
          </div>
          <p class="gs-state-title">What are you looking for?</p>
          <p class="gs-state-sub">Search employees, attendance, leaves, assets &amp; letters</p>
        </div>

        <!-- Too short -->
        <div v-else-if="query.length < 2" class="gs-state">
          <p class="gs-state-sub">Type at least 2 characters…</p>
        </div>

        <!-- Searching (first load — no prior results) -->
        <div v-else-if="loading && !hasAnyResults" class="gs-state">
          <a-spin size="large" />
          <p class="gs-state-sub">Searching for <strong>"{{ query }}"</strong>…</p>
        </div>

        <!-- No results -->
        <div v-else-if="!loading && !hasAnyResults" class="gs-state">
          <div class="gs-state-icon-ring gs-state-icon-ring--muted">
            <i class="fa-solid fa-face-meh"></i>
          </div>
          <p class="gs-state-title">No results</p>
          <p class="gs-state-sub">Nothing matched <strong>"{{ query }}"</strong></p>
        </div>

        <!-- Results -->
        <template v-else>
          <div
            v-for="modKey in visibleModules"
            :key="modKey"
            class="gs-section"
          >
            <!-- Section header -->
            <div class="gs-section-header">
              <div class="gs-section-label-row">
                <i :class="['gs-section-icon', MODULE_ICONS[modKey]]"></i>
                <span class="gs-section-label">{{ MODULE_LABELS[modKey] }}</span>
              </div>
              <span class="gs-section-count">{{ moduleData(modKey).total }}</span>
            </div>

            <!-- Expanded loading -->
            <div v-if="expandedModule === modKey && expandLoading" class="gs-section-loading">
              <a-spin size="small" />
              <span>Loading…</span>
            </div>

            <!-- Rows -->
            <template v-else>
              <search-result-item
                v-for="(item, i) in displayItems(modKey)"
                :key="i"
                :item="item"
                :module="modKey"
                :is-focused="getItemFlatIndex(modKey, i) === focusedIndex"
                @select="onSelect"
              />
            </template>

            <!-- Footer actions -->
            <div v-if="hasModuleActions(modKey)" class="gs-section-footer">
              <button
                v-if="expandedModule !== modKey && moduleData(modKey).has_more"
                class="gs-view-all-btn"
                @click="expandModule(modKey)"
              >
                <i class="fa-solid fa-layer-group"></i>
                View all {{ moduleData(modKey).total }} {{ MODULE_LABELS[modKey].toLowerCase() }}
              </button>
              <template v-if="expandedModule === modKey">
                <button
                  v-if="canLoadMore(modKey)"
                  class="gs-view-all-btn"
                  :disabled="expandLoading"
                  @click="loadMoreExpanded(modKey)"
                >
                  <i class="fa-solid fa-chevron-down"></i>
                  Load more
                </button>
                <button class="gs-collapse-btn" @click="collapseModule">
                  <i class="fa-solid fa-chevron-up"></i>
                  Show less
                </button>
              </template>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer hint -->
      <div v-if="hasAnyResults || loading" class="gs-modal-footer">
        <span class="gs-hint"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span class="gs-hint"><kbd>Enter</kbd> open</span>
        <span class="gs-hint"><kbd>Esc</kbd> close</span>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { globalSearch } from "@/api/globalSearch";
import SearchResultItem from "./SearchResultItem.vue";
import debounceMixin from "@/mixins/debounce";

const MODULE_ORDER  = ["employees", "attendance", "leaves", "assets", "letters"];
const MODULE_LABELS = {
  employees:  "Employees",
  attendance: "Attendance",
  leaves:     "Leaves",
  assets:     "Assets & Services",
  letters:    "Letters & Incidents",
};
const MODULE_ICONS = {
  employees:  "fa-solid fa-users",
  attendance: "fa-solid fa-clock",
  leaves:     "fa-solid fa-calendar-days",
  assets:     "fa-solid fa-box-open",
  letters:    "fa-solid fa-file-lines",
};

export default {
  name: "GlobalSearch",

  components: { SearchResultItem },

  mixins: [debounceMixin],

  data() {
    return {
      MODULE_ORDER,
      MODULE_LABELS,
      MODULE_ICONS,
      visible: false,
      query: "",
      results: {},
      loading: false,
      debouncedSearch: null,

      expandedModule: null,
      expandedItems: [],
      expandedPage: 1,
      expandedTotal: 0,
      expandedTotalPages: 1,
      expandLoading: false,
      focusedIndex: -1,
    };
  },

  computed: {
    hasAnyResults() {
      return MODULE_ORDER.some((mod) => this.moduleHasResults(mod));
    },
    visibleModules() {
      return MODULE_ORDER.filter((mod) => this.moduleHasResults(mod));
    },
    flatItems() {
      return this.visibleModules.flatMap((mod) =>
        this.displayItems(mod).map((item) => ({ ...item, module: item.module || mod }))
      );
    },
  },

  created() {
    this.debouncedSearch = this.debounce(this.executeSearch, 350);
    window.addEventListener("keydown", this.onGlobalKey);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.onGlobalKey);
  },

  methods: {
    onGlobalKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        this.openModal();
      }
    },

    openModal() {
      this.query = "";
      this.results = {};
      this.loading = false;
      this.expandedModule = null;
      this.expandedItems = [];
      this.expandedPage = 1;
      this.focusedIndex = -1;
      this.visible = true;
      this.$nextTick(() => this.$refs.inputRef?.focus());
    },

    closeModal() {
      this.visible = false;
      this.query = "";
      this.results = {};
      this.loading = false;
      this.expandedModule = null;
      this.expandedItems = [];
      this.expandedPage = 1;
      this.focusedIndex = -1;
    },

    onSelect(item) {
      const route = this.resolveRoute(item.module, item);
      this.closeModal();
      if (route) {
        this.$router.push(route);
      }
    },

    resolveRoute(mod, item) {
      const userPerms = (this.$store.getters["auth/userPermissions"] || []).map((p) => p.name ?? p);
      const has = (perm) => userPerms.includes(perm);
      const level = this.results[mod]?.access_level || item.access_level;

      const chain = (lvl) => {
        if (lvl === "full") return ["full", "team", "self"];
        if (lvl === "team") return ["team", "self"];
        return ["self"];
      };

      switch (mod) {
        case "employees": {
          if (level === "self") return { name: "profile" };
          return { name: "user-profile", params: { id: item.id } };
        }

        case "attendance":
          for (const l of chain(level)) {
            if (l === "full" && has("attendance_history-list"))
              return { name: "attendance-admin", query: { employee_id: item.employee_id, day: item.day } };
            if (l === "team" && has("my_team_dashboard-all-employee-leave-team"))
              return { name: "team-attendance", query: { employee_id: item.employee_id, day: item.day } };
            if (l === "self" && has("ess-attendance-employee"))
              return { name: "my-attendance", query: { day: item.day } };
          }
          return null;

        case "leaves": {
          const nameParts = [item.first_name, item.middle_name, item.last_name].filter(Boolean);
          const baseName = item.name || (nameParts.length ? nameParts.join(" ") : item.hcm_id || "");
          const employeeName = item.salutation ? `${item.salutation} ${baseName}` : baseName;
          const leaveStatus = item.status?.toLowerCase();
          for (const l of chain(level)) {
            if (l === "full" && has("leave_history-list"))
              return { name: "leave-history", query: { employee: employeeName, status: leaveStatus } };
            if (l === "team" && has("my_team_dashboard-all-employee-leave-team"))
              return { name: "team-leave", query: { employee: employeeName } };
            if (l === "self" && has("ess-leave"))
              return { name: "my-leaves" };
          }
          return null;
        }

        case "assets": {
          const assetType = item.matched?.[0]?.type;
          const assetEmployeeName = item.salutation ? `${item.salutation} ${item.name}` : item.name;
          const SELF_TAB = { asset: "asset", card: "card", service: "service", inventory: "inventory" };
          for (const l of chain(level)) {
            if (l === "full") {
              if (assetType === "card")
                return { name: "card", query: { employee: assetEmployeeName } };
              if (assetType === "service")
                return { name: "company-services", query: { employee: assetEmployeeName } };
              if (assetType === "inventory")
                return { name: "inventory", query: { employee: assetEmployeeName } };
              return { name: "assets", query: { employee: assetEmployeeName, tab: "records" } };
            }
            if (l === "self")
              return { name: "my-assets", query: { tab: SELF_TAB[assetType] || "asset" } };
          }
          return null;
        }

        case "letters": {
          const baseName = item.name || item.hcm_id || "";
          const employeeName = item.salutation ? `${item.salutation} ${baseName}` : baseName;
          const letterStatus = (item.status || "pending").toLowerCase();
          for (const l of chain(level)) {
            if (l === "full" && has("incident-list"))
              return { name: "all-incidents", query: { status: letterStatus, name: employeeName } };
            if (l === "self" && has("my-letter-requests-list"))
              return { name: "my-letter" };
          }
          return null;
        }

        default:
          return null;
      }
    },

    getItemFlatIndex(mod, localIdx) {
      let offset = 0;
      for (const m of this.visibleModules) {
        if (m === mod) return offset + localIdx;
        offset += this.displayItems(m).length;
      }
      return -1;
    },

    moveFocus(dir) {
      if (!this.flatItems.length) return;
      const next = this.focusedIndex + dir;
      if (next < 0) {
        this.focusedIndex = -1;
        return;
      }
      this.focusedIndex = Math.min(next, this.flatItems.length - 1);
      this.$nextTick(() => {
        const body = this.$refs.modalBody;
        const focused = body?.querySelector(".search-result-item.is-focused");
        if (!focused || !body) return;
        const bodyRect    = body.getBoundingClientRect();
        const focusedRect = focused.getBoundingClientRect();
        if (focusedRect.bottom > bodyRect.bottom) {
          body.scrollTop += focusedRect.bottom - bodyRect.bottom;
        } else if (focusedRect.top < bodyRect.top) {
          body.scrollTop -= bodyRect.top - focusedRect.top;
        }
      });
    },

    selectFocused() {
      if (this.focusedIndex >= 0 && this.flatItems[this.focusedIndex]) {
        this.onSelect(this.flatItems[this.focusedIndex]);
      }
    },

    onModalClose() {
      this.query = "";
      this.results = {};
      this.loading = false;
      this.expandedModule = null;
      this.expandedItems = [];
      this.expandedPage = 1;
      this.focusedIndex = -1;
    },

    clearSearch() {
      this.query = "";
      this.results = {};
      this.expandedModule = null;
      this.focusedIndex = -1;
      this.$nextTick(() => this.$refs.inputRef?.focus());
    },

    onInput() {
      this.expandedModule = null;
      this.expandedItems = [];
      this.focusedIndex = -1;
      if (this.query.trim().length < 2) {
        this.results = {};
        this.loading = false;
        return;
      }
      this.loading = true;
      this.debouncedSearch(this.query.trim());
    },

    async executeSearch(q) {
      try {
        const res = await globalSearch({ q, perPage: 5, page: 1 });
        this.results = res.data?.data || {};
      } catch (err) {
        this.results = {};
        console.error("[GlobalSearch]", err);
      } finally {
        this.loading = false;
      }
    },

    moduleData(mod) {
      return this.results[mod] || null;
    },

    filterModuleItems(mod, items) {
      if (mod === "assets") {
        return items.filter((item) => item.matched?.length > 0);
      }
      return items;
    },

    moduleHasResults(mod) {
      const d = this.moduleData(mod);
      if (!d || !Array.isArray(d.data)) return false;
      return this.filterModuleItems(mod, d.data).length > 0;
    },

    hasModuleActions(mod) {
      if (!this.moduleData(mod)) return false;
      if (this.expandedModule !== mod && this.moduleData(mod).has_more) return true;
      if (this.expandedModule === mod) return true;
      return false;
    },

    displayItems(mod) {
      const items = this.expandedModule === mod
        ? this.expandedItems
        : this.moduleData(mod)?.data || [];
      return this.filterModuleItems(mod, items);
    },

    async expandModule(mod) {
      this.expandedModule = mod;
      this.expandedItems = [];
      this.expandedPage = 1;
      await this.loadExpandedPage(mod, 1);
    },

    async loadMoreExpanded(mod) {
      if (this.canLoadMore(mod)) {
        await this.loadExpandedPage(mod, this.expandedPage + 1);
      }
    },

    async loadExpandedPage(mod, page) {
      this.expandLoading = true;
      try {
        const res = await globalSearch({ q: this.query.trim(), modules: [mod], perPage: 20, page });
        const data = res.data?.data?.[mod];
        if (data) {
          this.expandedItems = page === 1
            ? data.data || []
            : [...this.expandedItems, ...(data.data || [])];
          this.expandedPage       = data.current_page  || page;
          this.expandedTotal      = data.total         || this.expandedTotal;
          this.expandedTotalPages = data.total_pages   || this.expandedTotalPages;
        }
      } catch (err) {
        console.error("[GlobalSearch expand]", err);
      } finally {
        this.expandLoading = false;
      }
    },

    canLoadMore() {
      return this.expandedPage < this.expandedTotalPages;
    },

    collapseModule() {
      this.expandedItems  = this.moduleData(this.expandedModule)?.data || [];
      this.expandedModule = null;
      this.expandedPage   = 1;
    },
  },
};
</script>

<style>
/* ── Overlay z-index fix ─────────────────────────────────── */
.gs-modal-wrap { z-index: 1200 !important; }
.gs-modal-wrap + .ant-modal-mask { z-index: 1199 !important; }
body > .ant-modal-mask { z-index: 1199 !important; }

/* ── Modal shell ─────────────────────────────────────────── */
.gs-modal-wrap .ant-modal-content {
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.07);
}
.gs-modal-wrap .ant-modal-body { padding: 0; }

/* ── Trigger bar ─────────────────────────────────────────── */
.gs-trigger-wrap { display: flex; align-items: center; }
.gs-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(4px);
  transition: border-color 0.15s, box-shadow 0.15s;
  min-width: 210px;
}
.gs-trigger:hover {
  border-color: var(--primary-color, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79,70,229,0.08);
}
.gs-trigger-icon { color: #6b7280; font-size: 12px; }
.gs-trigger-text { flex: 1; font-size: 13px; color: #6b7280; }
.gs-trigger-kbd {
  font-size: 10px;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  padding: 1px 6px;
  font-family: monospace;
  background: #f3f4f6;
}

/* ── Header / input ──────────────────────────────────────── */
.gs-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.gs-modal-icon { color: #6b7280; font-size: 16px; flex-shrink: 0; }
.gs-modal-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111827;
  background: transparent;
  font-weight: 400;
}
.gs-modal-input::placeholder { color: #9ca3af; }
.gs-modal-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.gs-icon-btn {
  border: none;
  background: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 16px;
  padding: 2px;
  line-height: 1;
  transition: color 0.15s;
}
.gs-icon-btn:hover { color: #374151; }
.gs-esc-kbd {
  font-size: 11px;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  padding: 2px 7px;
  font-family: monospace;
  background: #f3f4f6;
  cursor: pointer;
  transition: border-color 0.15s;
}
.gs-esc-kbd:hover { border-color: #6b7280; }

/* ── Body ────────────────────────────────────────────────── */
.gs-modal-body {
  max-height: 480px;
  overflow-y: auto;
}
.gs-modal-body::-webkit-scrollbar { width: 5px; }
.gs-modal-body::-webkit-scrollbar-track { background: transparent; }
.gs-modal-body::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }

/* ── States ──────────────────────────────────────────────── */
.gs-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 52px 24px;
  gap: 10px;
  text-align: center;
}
.gs-state-icon-ring {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: var(--primary-color, #4f46e5);
  margin-bottom: 4px;
}
.gs-state-icon-ring--muted { background: #f3f4f6; color: #6b7280; }
.gs-state-title { font-size: 15px; font-weight: 600; color: #111827; margin: 0; }
.gs-state-sub   { font-size: 13px; color: #6b7280; margin: 0; }

/* ── Sections ────────────────────────────────────────────── */
.gs-section { border-bottom: 1px solid #e5e7eb; }
.gs-section:last-child { border-bottom: none; }

.gs-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 6px;
  background: #f9fafb;
}
.gs-section-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.gs-section-icon  { font-size: 11px; color: #6b7280; }
.gs-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #4b5563;
}
.gs-section-count {
  font-size: 11px;
  font-weight: 600;
  background: #e5e7eb;
  color: #374151;
  border-radius: 20px;
  padding: 1px 9px;
  min-width: 24px;
  text-align: center;
}
.gs-section-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 13px;
  color: #6b7280;
}

/* ── Section footer (view all / load more) ───────────────── */
.gs-section-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 20px 12px;
}
.gs-view-all-btn {
  border: none;
  background: none;
  color: var(--primary-color, #4f46e5);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: opacity 0.15s;
}
.gs-view-all-btn:hover   { opacity: 0.75; }
.gs-view-all-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.gs-collapse-btn {
  border: none;
  background: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  transition: color 0.15s;
}
.gs-collapse-btn:hover { color: #111827; }

/* ── Result rows ─────────────────────────────────────────── */
.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 20px;
  cursor: pointer;
  transition: background 0.1s;
}
.search-result-item:hover    { background: #f3f4f6; }
.search-result-item.is-focused { background: #eff6ff; }

.result-avatar {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.result-body   { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.result-primary {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-secondary {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

/* ── Status badges ───────────────────────────────────────── */
.gs-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 600;
  display: inline-block;
  white-space: nowrap;
  flex-shrink: 0;
}
.gs-badge-success   { background: #bbf7d0; color: #14532d; }
.gs-badge-warning   { background: #fde68a; color: #78350f; }
.gs-badge-danger    { background: #fecaca; color: #7f1d1d; }
.gs-badge-secondary { background: #e5e7eb; color: #1f2937; }

/* ── Footer hint bar ─────────────────────────────────────── */
.gs-modal-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}
.gs-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #4b5563;
}
.gs-hint kbd {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  font-family: monospace;
  background: #fff;
  color: #374151;
}
</style>
